import { useCallback, useEffect, useState } from "react";
import { Flex, Image, Overlay, ThemeIcon } from "@mantine/core";
import { IconArrowBackUp, IconArrowForwardUp, IconDownload, IconX } from "@tabler/icons-react";
import { File } from "src/graphql/generated/graphql";
import { handleError } from "src/utils/error";

import { downloadImage } from "./downloadUtils";

interface LightBoxProps {
    images: File[];
    selectedIndex: number;
    onClose: () => void;
    handleDownloadImage?: (image: File) => void;
}

export const LightBox = ({ images, selectedIndex, handleDownloadImage, onClose }: LightBoxProps) => {
    const [selectedImageIndex, setSelectedImageIndex] = useState<number>(selectedIndex);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);

    useEffect(() => {
        setSelectedImageIndex(selectedIndex);
    }, [selectedIndex]);

    const handleNext = useCallback(() => {
        if (selectedImageIndex === images?.length - 1) {
            return setSelectedImageIndex(0);
        }
        setSelectedImageIndex((selectedImageIndex) => selectedImageIndex + 1);
    }, [images, selectedImageIndex]);

    const handlePrevious = useCallback(() => {
        if (selectedImageIndex === 0) {
            return setSelectedImageIndex(images?.length - 1);
        }
        setSelectedImageIndex((selectedImageIndex) => selectedImageIndex - 1);
    }, [images, selectedImageIndex]);

    useEffect(() => {
        if (selectedImageIndex < 0) {
            return;
        }
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "ArrowRight") {
                handleNext();
            }
            if (event.key === "ArrowLeft") {
                handlePrevious();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleNext, handlePrevious, selectedImageIndex]);

    return (
        <Overlay fixed opacity={0.8} p={32} onClick={onClose}>
            <Image
                onClick={(event) => event?.stopPropagation?.()}
                radius={20}
                src={images[selectedImageIndex]?.src + "?w=2400"}
                alt="Review photo"
                width="80%"
                height="80%"
                pos="absolute"
                top="50%"
                left="50%"
                sx={{
                    transform: "translate(-50%, -50%)",
                }}
            />
            <Flex style={{ position: "absolute", right: 20, top: 20 }}>
                <ThemeIcon onClick={onClose} radius="xl" size={48} color="white" style={{ cursor: "pointer" }}>
                    <IconX color="red" size={24} />
                </ThemeIcon>
            </Flex>
            <Flex
                onClick={(e) => e.stopPropagation()}
                gap={12}
                direction="column"
                style={{
                    position: "absolute",
                    right: 20,
                    bottom: 20,
                }}
            >
                <ThemeIcon
                    onClick={handleNext}
                    style={{
                        cursor: "pointer",
                    }}
                    radius="xl"
                    size={48}
                    color="white"
                >
                    <IconArrowForwardUp stroke={1.5} color="black" size={24} />
                </ThemeIcon>
                <ThemeIcon
                    onClick={handlePrevious}
                    style={{
                        cursor: "pointer",
                    }}
                    radius="xl"
                    size={48}
                    color="white"
                >
                    <IconArrowBackUp stroke={1.5} color="black" size={24} />
                </ThemeIcon>
                <ThemeIcon
                    size={48}
                    radius="xl"
                    style={{
                        cursor: isDownloading ? "not-allowed" : "pointer",
                        opacity: isDownloading ? 0.6 : 1,
                    }}
                    onClick={async () => {
                        if (isDownloading) { return; };

                        setIsDownloading(true);
                        try {
                            if (handleDownloadImage) {
                                handleDownloadImage(images[selectedImageIndex]);
                                // Add delay for consistency with the downloadImage function
                                await new Promise(resolve => setTimeout(resolve, 500));
                            } else {
                                await downloadImage(images[selectedImageIndex], "?w=2400");
                            }
                        } catch (err) {
                            handleError(err, { context: { imageId: images[selectedImageIndex]?.id } });
                        } finally {
                            setIsDownloading(false);
                        }
                    }}
                >
                    <IconDownload stroke={1.5} size={24} />
                </ThemeIcon>
            </Flex>
        </Overlay>
    );
};
